import { Box, Typography, Button, Pagination, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { SystemModelMap, SystemVariantMap, type SystemAssigned } from '@consts/systems';
import { AppRoutes } from '@consts/routes';
import { useNavigate } from 'react-router-dom';
import { useNameMapQuery } from '@features/redux/apis/org';
import { useSystemsQuery, useSystemsBySerialNumQuery } from '@features/redux/apis/system';
import { SystemTableEffects } from './SystemTableEffects';
import { useSelector } from 'react-redux';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import type { SystemFilter } from '@/types/systems';
import type { System } from '@api/system';
import type { Location } from '@/types/locations';
import type { RootState } from '@features/redux/store';
import { ExportSystems } from '@components/buttons/ExportSystems';
import { getFilterParams } from '@utils/filters';
import { type BillingType, CUT_FEE_MAP, type WarrantyType, WARRANTY_PLAN_MAP } from '@consts/company';
import { colors, DataTable, TableCard, TableLink, ConnectionIndicator, StatusBadge } from '@components/design-system';
import { typography as typographyTokens, radii } from '@components/design-system/tokens';
import { MobileSystemCard, MobileSystemCardSkeleton } from './MobileSystemCard';

const PAGE_SIZE = 12;

interface SystemTableProps {
    formattedLocations?: Location[];
    assigned: SystemAssigned;
    searchTerm: string;
    filters: SystemFilter;
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
}

export const SystemTable = ({ formattedLocations, assigned, searchTerm, filters, orgFilters }: SystemTableProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const [pageNum, setPageNum] = useState(1);

    const searchQuery = useSystemsBySerialNumQuery(
        {
            serialNum: searchTerm,
        },
        { skip: nameMapLoading || !searchTerm }
    );
    const { data: searchData, isFetching: searchFetching } = searchQuery;

    const systemsQuery = useSystemsQuery(
        {
            ...orgFilters,
            ...getFilterParams(filters),
            type: assigned,
            pageNum: pageNum - 1,
            pageSize: PAGE_SIZE,
        },
        { skip: nameMapLoading || Boolean(searchTerm) }
    );
    const { data: systemsData, isFetching: systemsFetching } = systemsQuery;

    const systems = searchTerm ? searchData : systemsData?.data;
    const loading = nameMapLoading || (searchTerm ? searchFetching : systemsFetching);
    const totalRecords = searchTerm ? searchData?.length : systemsData?.total;

    const columns = useMemo(
        () => [
            {
                field: 'LocationID',
                headerName: 'Location',
                flex: 1,
                minWidth: 140,
                valueGetter: ({ value }: { value: number }) => nameMap?.locations[value] || '',
                renderCell: ({ row }: { row: System }) =>
                    row.LocationID ? (
                        <TableLink to={`${AppRoutes.Organizations}?type=location&id=${row.LocationID}`}>
                            {nameMap?.locations[row.LocationID as number]}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'CompanyID',
                headerName: 'Company',
                flex: 1,
                minWidth: 140,
                valueGetter: ({ value }: { value: number }) => nameMap?.companies[value] || '',
                renderCell: ({ row }: { row: System }) =>
                    row.CompanyID ? (
                        <TableLink to={`${AppRoutes.Organizations}?type=company&id=${row.CompanyID}`}>
                            {nameMap?.companies[row.CompanyID as number]}
                        </TableLink>
                    ) : (
                        <Typography sx={{ color: colors.neutral[400], fontSize: '0.8125rem' }}>-</Typography>
                    ),
            },
            {
                field: 'SerialNum',
                headerName: 'Serial Number',
                flex: 1,
                minWidth: 130,
                renderCell: ({ value }: { value: string }) => (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: colors.neutral[700] }}>
                        {value}
                    </Typography>
                ),
            },
            {
                field: 'ProductID',
                headerName: 'Model',
                flex: 0.8,
                minWidth: 100,
                valueGetter: ({ value }: { value: number }) => SystemVariantMap[value] || '',
            },
            {
                field: 'CreatedDate',
                headerName: 'Created',
                flex: 1,
                minWidth: 110,
                renderCell: ({ value }: { value: string }) => {
                    if (!value) return '-';
                    return new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                    }).format(new Date(value));
                },
            },
            {
                field: 'Status',
                headerName: 'Status',
                flex: 0.8,
                minWidth: 100,
                renderCell: ({ value }: { value: string | null }) => {
                    const statusMap: Record<string, { label: string; status: 'success' | 'warning' | 'error' }> = {
                        Active: { label: 'Active', status: 'success' },
                        Inactive: { label: 'Inactive', status: 'warning' },
                        Decommissioned: { label: 'Decommissioned', status: 'error' },
                    };
                    const config = (value && statusMap[value]) || { label: 'Unknown', status: 'neutral' as const };
                    return <StatusBadge label={config.label} status={config.status} size='small' />;
                },
            },
            {
                field: 'AppVersion',
                headerName: 'Version',
                flex: 0.7,
                minWidth: 90,
                renderCell: ({ value }: { value: string | null }) => (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: colors.neutral[700] }}>
                        {value || '-'}
                    </Typography>
                ),
            },
            {
                field: 'LastConnected',
                headerName: 'Last Connected',
                flex: 1,
                minWidth: 130,
                renderCell: ({ value }: { value: string }) => <ConnectionIndicator timestamp={value} />,
            },
            {
                field: 'CutPlanID',
                headerName: 'Billing Plan',
                flex: 1.2,
                minWidth: 150,
                renderCell: ({ value }: { value: BillingType }) =>
                    value !== undefined && value !== null ? CUT_FEE_MAP[value] || '-' : '-',
            },
        ],
        [nameMap]
    );

    const startItem = (pageNum - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(pageNum * PAGE_SIZE, totalRecords || 0);
    const infoText =
        totalRecords && totalRecords > 0
            ? `Showing ${startItem}-${endItem} of ${totalRecords} systems`
            : 'No systems found';

    const totalPages = Math.ceil((totalRecords || 0) / PAGE_SIZE);

    // Mobile card view
    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: colors.neutral[100],
                    overflow: 'hidden',
                }}>
                <SystemTableEffects queries={[searchQuery, systemsQuery]} />

                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        backgroundColor: colors.neutral[0],
                        borderBottom: `1px solid ${colors.neutral[200]}`,
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.sm,
                                color: colors.neutral[600],
                            }}>
                            {infoText}
                        </Typography>
                        <ExportSystems type={assigned} params={orgFilters} />
                    </Box>
                </Box>

                {/* Cards List */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 2,
                        py: 2,
                    }}>
                    {loading ? (
                        Array.from({ length: 4 }).map((_, index) => <MobileSystemCardSkeleton key={index} />)
                    ) : systems && systems.length > 0 ? (
                        systems.map((system: System) => (
                            <MobileSystemCard
                                key={system.StationID}
                                system={system}
                                nameMap={nameMap}
                                onClick={
                                    role.stores.edit
                                        ? () =>
                                              navigate(`${AppRoutes.Organizations}?type=system&id=${system.StationID}`)
                                        : undefined
                                }
                            />
                        ))
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                color: colors.neutral[500],
                            }}>
                            <Typography>No systems found</Typography>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {!searchTerm && totalPages > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderTop: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Pagination
                            count={totalPages}
                            page={pageNum}
                            onChange={(_, newPage) => setPageNum(newPage)}
                            color='primary'
                            size='small'
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    borderRadius: `${radii.md}px`,
                                    minWidth: 32,
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    }

    // Desktop table view
    return (
        <Box
            display='flex'
            flexDirection='column'
            flex={1}
            sx={{
                minWidth: 0,
                minHeight: 0,
                height: '100%',
                backgroundColor: colors.neutral[100],
                p: 2,
            }}>
            <SystemTableEffects queries={[searchQuery, systemsQuery]} />

            <TableCard
                title='Systems'
                actions={<ExportSystems type={assigned} params={orgFilters} />}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <DataTable
                    rows={systems ?? []}
                    columns={columns as any}
                    getRowId={(row) => row.StationID?.toString() ?? ''}
                    totalRows={totalRecords || 0}
                    page={pageNum}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPageNum}
                    loading={loading}
                    showPagination={!searchTerm}
                    height='100%'
                    infoText={infoText}
                    onRowClick={
                        role.stores.edit
                            ? ({ row }) => navigate(`${AppRoutes.Organizations}?type=system&id=${row.StationID}`)
                            : undefined
                    }
                />
            </TableCard>
        </Box>
    );
};
